import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faClipboardList,
  faMobileScreen,
  faShirt,
  faMoneyBillWave,
  faIndianRupeeSign,
  faHospital,
  faXmark,
  faLeaf,
} from "@fortawesome/free-solid-svg-icons";
import { useReveal } from "../lib/motion";
import { useSection } from "../data/content";
import { resolveIcon } from "../data/iconMap";
import "./Facilities.css";

export const facilityCards = [
  {
    id: "rules",
    icon: faClipboardList,
    title: "General Rules & Conduct",
    summary: "Day-to-day conduct, room upkeep, visitor and leave policies.",
    sections: [
      {
        items: [
          "Students should cooperate in the upkeep of the Nurses Hostel and its good name.",
          "Rooms should be kept clean and tidy; students are fully responsible for furniture supplied to them.",
          "Proper locks should be put on cupboards and boxes. Jewellery and expensive items are not permitted in hostel rooms.",
          "Water in the Hostel is available 24 hours, but wastage and carelessness will render students to disciplinary action.",
          "Use of jewellery, cosmetics & bangles is strictly prohibited. Any student violating this norm will be fined Rs. 50.",
          "Relatives are not permitted inside the rooms.",
          "Students can go home for two days a month, provided they can be spared from hospital duty and no classes are missed. Parents must take prior written permission from the Principal, which can be refused if it is not in the interest of studies.",
          "In case of sickness, students should report to the Principal. All letters coming to students will be censored by the Warden.",
          "Parents are requested to check the progress of the student with class teachers on any Saturday.",
          "If parents wish that their children can travel alone, they should give their consent in writing to the Principal.",
          "Training can be discontinued in case of misconduct. Food shall be provided on the dining table.",
          "Four photographs of four relatives each, who can visit the student in the Hostel.",
          "Two photographs of male relatives who may, in case of emergency, visit the day scholar during school hours.",
          "The Principal and Director are fully authorized to effect changes in rules and regulations at any time.",
        ],
      },
      {
        heading: "Important Notes",
        emphasize: true,
        items: [
          "Failure to return in time will be dealt with seriously and strictly. Parents are requested to cooperate.",
          "Any item or question not covered by the above rules of the College & Hostel shall be decided by the management, whose decision will be final.",
        ],
      },
    ],
  },
  {
    id: "mobile",
    icon: faMobileScreen,
    title: "Mobile, ID Card & Dining",
    summary: "Phone policy, identity card rules and dining hall attendance.",
    sections: [
      {
        items: [
          "Carrying mobile phones in the Hostel / College is strictly prohibited.",
          "If a student is found with a mobile, it will be confiscated and the student will be fined a minimum of Rs. 10,000/-. She will not be allowed to attend classes for two weeks. Re-entry will only be permitted on the basis of a written apology and assurance from the student and parents that she will not carry the mobile again.",
          "Each student is issued an identity card which she must carry at all times and produce whenever asked by school authorities. No student will be allowed to enter the college premises without the identity card.",
          "Attendance of students in the dining hall every day is compulsory. In case of any issue, students should give a written complaint to the hostel warden.",
        ],
      },
    ],
  },
  {
    id: "dress",
    icon: faShirt,
    title: "Dress Code & Anti-Ragging",
    summary: "Acceptable dress on campus and the institute's anti-ragging policy.",
    sections: [
      {
        emphasize: true,
        items: [
          "Students are required to wear simple, decent dress. Boy shirts, skirts and other objectionable fashionable dresses are NOT allowed.",
          "Students are instructed to wear dupattas outside the hostel premises.",
          "Ragging is strictly prohibited. Anti-ragging guidelines as per gazette notification shall be followed.",
        ],
      },
    ],
  },
  {
    id: "payment",
    icon: faMoneyBillWave,
    title: "Fee Payment Rules",
    summary: "Due dates, late fines, extensions and consequences of non-payment.",
    sections: [
      {
        items: [
          "The date for payment of hostel dues for various classes is between the 1st and 10th of every month.",
          "A fine of Rs. 10/- will be charged every day for non-payment of dues in time.",
          "Non-payment of hostel dues within two months of the last day of payment will render the student liable to be struck off from the school rolls. Studentship can be restored by paying the re-admission fee and arrears.",
          "Extension in the date for payment of dues may be granted in exceptional cases by the Director. Applications must be made before the expiry of the due date.",
          "Hostel charges are subject to revision and will apply to new as well as senior students whenever such revision takes place.",
          "Hostel dues once paid will not be refunded even if the student leaves the hostel mid-session.",
          "The management has the right to change the rates of mess dues according to the prevalent market rates of essential commodities.",
        ],
      },
    ],
  },
  {
    id: "charges",
    icon: faIndianRupeeSign,
    title: "Hostel Charges",
    summary: "When and how monthly hostel fees should be paid.",
    sections: [
      {
        items: [
          "The date for the payment of hostel dues for various classes will be between the 1st and 10th of every month.",
        ],
      },
    ],
  },
  {
    id: "hospitals",
    icon: faHospital,
    title: "Hospitals for Practical Training",
    summary: "Affiliated hospitals where students complete their clinical training.",
    sections: [
      {
        items: [
          "Pulse Hospital",
          "EMC Super Speciality Hospital",
          "Punj Hospital",
          "Fortis Hospital",
          "Shoor Hospital",
          "Civil Hospital, Amritsar",
          "Dr. Vidya Sagar Mental Hospital, Amritsar",
          "Community Health Centre Chabal",
        ],
      },
    ],
  },
];

const FacilityCard = ({ card, onOpen }) => {
  const [ref, shown] = useReveal();
  return (
    <button
      ref={ref}
      type="button"
      className={`facility-card reveal ${shown ? "is-visible" : ""}`}
      onClick={onOpen}
    >
      <span className="facility-icon">
        <FontAwesomeIcon icon={resolveIcon(card.icon)} />
      </span>
      <h3 className="facility-title">{card.title}</h3>
      <p className="facility-summary">{card.summary}</p>
      <span className="facility-link">
        Read details <FontAwesomeIcon icon={faArrowRight} />
      </span>
    </button>
  );
};

const Facilities = () => {
  const [openId, setOpenId] = useState(null);
  // CMS-managed with the bundled cards as the verbatim fallback (identical until
  // edited). Ensure every card has a stable id for the open/modal lookup.
  const facilities = useSection("facilities", facilityCards);
  const cards = facilities.map((c, i) => (c.id ? c : { ...c, id: `facility-${i}` }));

  useEffect(() => {
    document.body.style.overflow = openId ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpenId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const activeCard = cards.find((c) => c.id === openId);

  return (
    <>
      <section className="page-hero">
        <span className="blob" aria-hidden="true" />
        <div className="container page-hero-inner">
          <span className="page-hero-icon">
            <FontAwesomeIcon icon={faHospital} />
          </span>
          <p className="eyebrow">Life at the hostel</p>
          <h1 className="page-hero-title">Hostel &amp; campus facilities</h1>
          <p className="page-hero-lead">
            Everything about life at our hostel — rules, dress code, fees and the
            hospitals where you'll train. Open a card to read the full details.
          </p>
        </div>
      </section>

      <section className="facilities">
        <div className="container facility-grid">
          {cards.map((card) => (
            <FacilityCard key={card.id} card={card} onOpen={() => setOpenId(card.id)} />
          ))}
        </div>
      </section>

      {activeCard && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="facility-modal-title"
          onClick={() => setOpenId(null)}
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-head-left">
                <span className="facility-icon facility-icon--sm">
                  <FontAwesomeIcon icon={resolveIcon(activeCard.icon)} />
                </span>
                <h2 id="facility-modal-title" className="modal-title">
                  {activeCard.title}
                </h2>
              </div>
              <button
                type="button"
                className="modal-close"
                aria-label="Close"
                onClick={() => setOpenId(null)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="modal-body">
              {activeCard.sections.map((section, sIdx) => (
                <div
                  key={sIdx}
                  className={section.emphasize ? "modal-section modal-section--note" : "modal-section"}
                >
                  {section.heading && <h4 className="modal-section-head">{section.heading}</h4>}
                  <ul className="modal-list">
                    {section.items.map((item, iIdx) => (
                      <li key={iIdx}>
                        <FontAwesomeIcon icon={faLeaf} className="modal-bullet" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Facilities;
